<template>
	<div class="document-markdown-editor">
		<textarea ref="textarea"></textarea>
	</div>
</template>

<script>
import EasyMDE from 'easymde';
import CodeMirror from 'codemirror';
import 'codemirror/lib/codemirror.css';
import 'easymde/dist/easymde.min.css';

export default {
	name: 'DocumentMarkdownEditor',
	props: {
		value: {
			type: String,
			default: '',
		},

		readonly: {
			type: Boolean,
			default: false,
		},

		elements: {
			type: Array,
			default: () => [],
		},
	},

	data() {
		return {
			editor: null,
			documentElementOverlay: null,
		};
	},

	mounted() {
		this.initializeEditor();
	},

	beforeDestroy() {
		if (!this.editor) {
			return;
		}

		this.editor.codemirror.off('change', this.handleEditorChange);

		this.editor.toTextArea();
		this.editor = null;
	},
	computed: {
		availableElementKeys() {
			const keys = new Set();

			this.elements.forEach(group => {
				(group.sections || []).forEach(section => {
					(section.entries || []).forEach(entry => {
						if (entry && typeof entry.key === 'string') {
							keys.add(entry.key);
						}
					});
				});
			});

			return keys;
		},
	},
	methods: {
		initializeEditor() {
			this.editor = new EasyMDE({
				element: this.$refs.textarea,
				initialValue: this.value || '',
				readOnly: this.readonly,
				spellChecker: false,
				status: false,
				autofocus: false,
				autoDownloadFontAwesome: false,
				toolbar: this.getToolbar(),
			});

			const codeMirror = this.editor.codemirror;
			codeMirror.setOption('readOnly', this.readonly);
			this.initializeDocumentElementHighlighting(codeMirror);
			codeMirror.on('change', this.handleEditorChange);
			this.initializeKeyboardHandling(codeMirror);
		},

		getToolbar() {
			if (this.readonly) {
				return [];
			}
			return [
				{
					name: 'bold',
					action: () => {
						this.toggleInlineFormat('**');
					},
					text: 'B',
					title: 'Fett',
				},

				{
					name: 'italic',
					action: () => {
						this.toggleInlineFormat('*');
					},
					text: 'I',
					title: 'Kursiv',
				},

				{
					name: 'heading',
					action: EasyMDE.toggleHeadingSmaller,
					text: 'H',
					title: 'Überschrift',
				},

				'|',

				{
					name: 'unordered-list',
					action: () => {
						this.toggleList('unordered');
					},
					text: '•',
					title: 'Aufzählung',
				},

				{
					name: 'ordered-list',
					action: () => {
						this.toggleList('ordered');
					},
					text: '1.',
					title: 'Nummerierte Liste',
				},

				'|',

				{
					name: 'undo',
					action: editor => {
						editor.codemirror.undo();
					},
					text: '↶',
					title: 'Rückgängig',
				},

				{
					name: 'redo',
					action: editor => {
						editor.codemirror.redo();
					},
					text: '↷',
					title: 'Wiederherstellen',
				},
			];
		},

		initializeKeyboardHandling(codeMirror) {
			const existingExtraKeys = codeMirror.getOption('extraKeys') || {};

			codeMirror.setOption('extraKeys', {
				...existingExtraKeys,

				Enter: cm => this.handleEnter(cm),

				Backspace: cm => this.handleBackspace(cm),

				'Cmd-Backspace': cm => this.handleDeleteLine(cm),

				'Ctrl-Backspace': cm => this.handleDeleteLine(cm),

				'Ctrl-B': () => {
					this.toggleInlineFormat('**');
				},

				'Cmd-B': () => {
					this.toggleInlineFormat('**');
				},

				'Ctrl-I': () => {
					this.toggleInlineFormat('*');
				},

				'Cmd-I': () => {
					this.toggleInlineFormat('*');
				},
			});
		},

		handleEditorChange(codeMirror, change) {
			if (!this.editor) {
				return;
			}

			if (change?.origin === '+document-list-normalize') {
				this.emitEditorValue();
				return;
			}

			if (change?.origin === '+input' || change?.origin === '+delete' || change?.origin === 'cut') {
				const changedLine = change.from?.line ?? 0;

				this.$nextTick(() => {
					if (!this.editor) {
						return;
					}

					this.normalizeOrderedListAroundLine(this.editor.codemirror, changedLine);
				});
			}

			this.emitEditorValue();
		},

		emitEditorValue() {
			if (!this.editor) {
				return;
			}

			const value = this.editor.value();

			this.$emit('input', value);

			this.$emit('change', value);
		},

		initializeDocumentElementHighlighting(codeMirror) {
			const documentElementOverlay = {
				token: stream => {
					const remainingText = stream.string.substring(stream.pos);

					if (!remainingText.startsWith('{{')) {
						stream.next();
						return null;
					}

					const match = remainingText.match(/^\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/);

					if (match && this.availableElementKeys.has(match[1])) {
						stream.pos += match[0].length;

						return 'document-element';
					}

					stream.next();

					return null;
				},
			};

			this.documentElementOverlay = documentElementOverlay;

			codeMirror.addOverlay(documentElementOverlay);
		},

		handleEnter(codeMirror) {
			if (codeMirror.somethingSelected()) {
				return CodeMirror.Pass;
			}

			const cursor = codeMirror.getCursor();
			const line = codeMirror.getLine(cursor.line);
			const listInfo = this.getListInfo(line);
			const listPrefixLength = this.getListPrefixLength(line);

			if (listInfo && !listInfo.content.trim()) {
				this.exitList(codeMirror, cursor.line, listInfo.type);

				return undefined;
			}

			if (listInfo && listInfo.content.trim() && cursor.ch <= listPrefixLength) {
				const originalLine = cursor.line;

				codeMirror.replaceRange(
					'\n',
					{
						line: originalLine,
						ch: 0,
					},
					{
						line: originalLine,
						ch: 0,
					},
					'+document-list-split',
				);

				const newListLine = originalLine + 1;

				if (listInfo.type === 'ordered') {
					this.renumberOrderedList(codeMirror, newListLine);
				}

				codeMirror.setCursor({
					line: originalLine,
					ch: 0,
				});

				return undefined;
			}

			const activeMarker = this.getActiveInlineMarker(line, cursor.ch, listPrefixLength);
			if (listInfo) {
				const nextPrefix = listInfo.type === 'ordered' ? `${listInfo.number + 1}. ` : '- ';

				const formatBoundary = activeMarker || '';

				codeMirror.replaceRange(`${formatBoundary}\n${listInfo.indent}${nextPrefix}${formatBoundary}`, cursor, cursor, '+document-list-enter');

				if (listInfo.type === 'ordered') {
					this.renumberOrderedList(codeMirror, cursor.line);
				}

				return undefined;
			}

			if (activeMarker) {
				codeMirror.replaceRange(`${activeMarker}\n${activeMarker}`, cursor, cursor, '+document-inline-enter');

				return undefined;
			}

			return CodeMirror.Pass;
		},

		handleBackspace(codeMirror) {
			if (codeMirror.somethingSelected()) {
				return CodeMirror.Pass;
			}

			const cursor = codeMirror.getCursor();

			const line = codeMirror.getLine(cursor.line);

			const info = this.getListInfo(line);

			if (!info) {
				return CodeMirror.Pass;
			}

			const prefixLength = this.getListPrefixLength(line);

			if (cursor.ch > 0 && cursor.ch <= prefixLength) {
				const result = this.removeSelectionFromList(codeMirror, cursor.line, cursor.line, info.type);

				if (result) {
					codeMirror.setCursor({
						line: result.plainStart,
						ch: 0,
					});
				}

				return undefined;
			}

			if (cursor.ch === 0 && cursor.line > 0) {
				const previousLineNumber = cursor.line - 1;
				const previousLine = codeMirror.getLine(previousLineNumber);

				if (previousLine.trim() === '' && previousLineNumber > 0) {
					const beforePrevious = codeMirror.getLine(previousLineNumber - 1);

					const previousInfo = this.getListInfo(beforePrevious);

					if (previousInfo && previousInfo.type === info.type) {
						codeMirror.replaceRange(
							'',
							{
								line: previousLineNumber,
								ch: 0,
							},
							{
								line: cursor.line,
								ch: 0,
							},
							'+document-list-rejoin',
						);

						const joinedLine = previousLineNumber;

						if (info.type === 'ordered') {
							this.renumberOrderedList(codeMirror, joinedLine);
						}

						codeMirror.setCursor({
							line: joinedLine,
							ch: this.getListPrefixLength(codeMirror.getLine(joinedLine)),
						});

						return undefined;
					}
				}

				const previousInfo = this.getListInfo(previousLine);

				if (previousInfo && previousInfo.type === info.type) {
					if (info.type === 'ordered') {
						this.renumberOrderedList(codeMirror, cursor.line);
					}

					return undefined;
				}
			}

			return CodeMirror.Pass;
		},

		handleDeleteLine(codeMirror) {
			if (codeMirror.somethingSelected()) {
				return CodeMirror.Pass;
			}

			const cursor = codeMirror.getCursor();
			const lineNumber = cursor.line;
			const line = codeMirror.getLine(lineNumber);
			const info = this.getListInfo(line);

			if (!info) {
				return CodeMirror.Pass;
			}

			if (lineNumber < codeMirror.lineCount() - 1) {
				codeMirror.replaceRange(
					'',
					{
						line: lineNumber,
						ch: 0,
					},
					{
						line: lineNumber + 1,
						ch: 0,
					},
					'+document-list-delete-line',
				);
			} else {
				codeMirror.replaceRange(
					'',
					{
						line: lineNumber,
						ch: 0,
					},
					{
						line: lineNumber,
						ch: line.length,
					},
					'+document-list-delete-line',
				);
			}

			if (info.type === 'ordered') {
				const candidateLines = [lineNumber, lineNumber - 1];

				for (const candidate of candidateLines) {
					if (candidate < 0 || candidate >= codeMirror.lineCount()) {
						continue;
					}

					const candidateInfo = this.getListInfo(codeMirror.getLine(candidate));

					if (candidateInfo && candidateInfo.type === 'ordered') {
						this.renumberOrderedList(codeMirror, candidate);

						break;
					}
				}
			}

			const finalLine = Math.min(lineNumber, codeMirror.lineCount() - 1);

			codeMirror.setCursor({
				line: Math.max(0, finalLine),
				ch: 0,
			});

			return undefined;
		},

		toggleInlineFormat(marker) {
			if (!this.editor) {
				return;
			}

			const codeMirror = this.editor.codemirror;

			let from = codeMirror.getCursor('from');
			let to = codeMirror.getCursor('to');

			if (from.line === to.line && from.ch === to.ch) {
				return;
			}

			let selection = this.expandSelectionToDocumentElements(codeMirror, from, to);
			selection = this.normalizeInlineFormatSelection(codeMirror, selection.from, selection.to, marker);
			selection = this.expandSelectionToInlineFormat(codeMirror, selection.from, selection.to, marker);

			from = selection.from;
			to = selection.to;

			const lastLine = to.ch === 0 && to.line > from.line ? to.line - 1 : to.line;

			const normalizedTo = {
				line: lastLine,
				ch: lastLine === to.line ? to.ch : codeMirror.getLine(lastLine).length,
			};

			const removeFormat = this.areAllSelectedRangesFormatted(codeMirror, from, normalizedTo, marker);

			for (let lineNumber = lastLine; lineNumber >= from.line; lineNumber--) {
				const line = codeMirror.getLine(lineNumber);
				let startCh = lineNumber === from.line ? from.ch : 0;
				const endCh = lineNumber === normalizedTo.line ? normalizedTo.ch : line.length;
				startCh = Math.max(startCh, this.getListPrefixLength(line));

				if (endCh <= startCh) {
					continue;
				}

				const selectedText = line.substring(startCh, endCh);

				if (!selectedText.trim()) {
					continue;
				}

				if (removeFormat) {
					this.removeInlineFormatForRange(codeMirror, lineNumber, line, startCh, endCh, marker);

					continue;
				}

				if (this.isRangeInsideInlineFormat(line, startCh, endCh, marker)) {
					continue;
				}

				codeMirror.replaceRange(
					this.normalizeInlineSelection(selectedText, marker),
					{
						line: lineNumber,
						ch: startCh,
					},
					{
						line: lineNumber,
						ch: endCh,
					},
					'+document-inline-format',
				);
			}

			const finalLine = Math.min(lastLine, codeMirror.lineCount() - 1);

			codeMirror.setCursor({
				line: finalLine,
				ch: codeMirror.getLine(finalLine).length,
			});

			codeMirror.focus();
		},

		areAllSelectedRangesFormatted(codeMirror, from, to, marker) {
			let hasContent = false;

			for (let lineNumber = from.line; lineNumber <= to.line; lineNumber++) {
				const line = codeMirror.getLine(lineNumber);
				let startCh = lineNumber === from.line ? from.ch : 0;
				const endCh = lineNumber === to.line ? to.ch : line.length;
				startCh = Math.max(startCh, this.getListPrefixLength(line));

				if (endCh <= startCh) {
					continue;
				}

				const selectedText = line.substring(startCh, endCh);

				if (!selectedText.trim()) {
					continue;
				}

				hasContent = true;

				if (this.isExactlyFormattedSelection(selectedText, marker)) {
					continue;
				}

				if (this.isRangeInsideInlineFormat(line, startCh, endCh, marker)) {
					continue;
				}

				return false;
			}

			return hasContent;
		},

		isRangeInsideInlineFormat(line, startCh, endCh, marker) {
			const sections = this.findInlineFormatSections(line, marker);

			return sections.some(section => {
				return startCh >= section.contentStart && endCh <= section.contentEnd;
			});
		},

		getInlineContentSelection(selectionStart, selectionEnd, section) {
			return {
				start: Math.max(selectionStart, section.contentStart),

				end: Math.min(selectionEnd, section.contentEnd),
			};
		},

		findInlineFormatSection(line, selectionStart, selectionEnd, marker) {
			const sections = this.findInlineFormatSections(line, marker);

			for (const section of sections) {
				const overlaps = selectionStart < section.end && selectionEnd > section.start;
				const cursorInside = selectionStart === selectionEnd && selectionStart >= section.contentStart && selectionStart <= section.contentEnd;

				if (!overlaps && !cursorInside) {
					continue;
				}

				return {
					open: section.start,
					close: section.contentEnd,
					sectionStart: section.start,
					sectionEnd: section.end,
					contentStart: section.contentStart,
					contentEnd: section.contentEnd,
				};
			}

			return null;
		},

		getActiveInlineMarker(line, cursorCh, minimumCh = 0) {
			const bold = this.findInlineFormatSection(line, cursorCh, cursorCh, '**');

			if (bold && bold.open >= minimumCh) {
				return '**';
			}

			const italic = this.findInlineFormatSection(line, cursorCh, cursorCh, '*');

			if (italic && italic.open >= minimumCh) {
				return '*';
			}

			return null;
		},

		removeInlineFormatForRange(codeMirror, lineNumber, line, startCh, endCh, marker) {
			const selectedText = line.substring(startCh, endCh);

			if (this.isExactlyFormattedSelection(selectedText, marker)) {
				codeMirror.replaceRange(
					this.removeOuterInlineFormat(selectedText, marker),
					{
						line: lineNumber,
						ch: startCh,
					},
					{
						line: lineNumber,
						ch: endCh,
					},
					'+document-inline-format',
				);

				return;
			}

			const section = this.findContainingInlineFormatSection(line, startCh, endCh, marker);

			if (!section) {
				return;
			}

			if (startCh <= section.contentStart && endCh >= section.contentEnd) {
				codeMirror.replaceRange(
					line.substring(section.contentStart, section.contentEnd),
					{
						line: lineNumber,
						ch: section.sectionStart,
					},
					{
						line: lineNumber,
						ch: section.sectionEnd,
					},
					'+document-inline-format',
				);

				return;
			}

			const beforeContent = line.substring(section.contentStart, startCh);
			const selectedContent = line.substring(startCh, endCh);
			const afterContent = line.substring(endCh, section.contentEnd);
			let replacement = '';
			replacement += this.wrapInlineFormatPart(beforeContent, marker);
			replacement += selectedContent;
			replacement += this.wrapInlineFormatPart(afterContent, marker);
			codeMirror.replaceRange(
				replacement,
				{
					line: lineNumber,
					ch: section.sectionStart,
				},
				{
					line: lineNumber,
					ch: section.sectionEnd,
				},
				'+document-inline-format',
			);
		},

		isExactlyFormattedSelection(value, marker) {
			if (!value || !value.trim()) {
				return false;
			}

			const { leadingWhitespace, content } = this.splitOuterWhitespace(value);

			if (!content.startsWith(marker) || !content.endsWith(marker) || content.length < marker.length * 2) {
				return false;
			}

			const inner = content.substring(marker.length, content.length - marker.length);

			if (this.containsSameInlineMarker(inner, marker)) {
				return false;
			}

			return leadingWhitespace.length <= value.length;
		},

		removeOuterInlineFormat(value, marker) {
			const { leadingWhitespace, trailingWhitespace, content } = this.splitOuterWhitespace(value);

			return leadingWhitespace + content.substring(marker.length, content.length - marker.length) + trailingWhitespace;
		},

		normalizeInlineSelection(value, marker) {
			if (!value || !value.trim()) {
				return value;
			}

			const { leadingWhitespace, trailingWhitespace, content } = this.splitOuterWhitespace(value);

			const normalizedContent = this.removeSameInlineMarkers(content, marker);

			if (!normalizedContent.trim()) {
				return value;
			}

			return leadingWhitespace + marker + normalizedContent + marker + trailingWhitespace;
		},

		splitOuterWhitespace(value) {
			const leadingWhitespace = value.match(/^\s*/)?.[0] || '';
			const trailingWhitespace = value.match(/\s*$/)?.[0] || '';
			const content = value.substring(leadingWhitespace.length, value.length - trailingWhitespace.length);

			return {
				leadingWhitespace,
				trailingWhitespace,
				content,
			};
		},

		removeSameInlineMarkers(value, marker) {
			if (marker === '**') {
				return value.split('**').join('');
			}

			if (marker === '*') {
				const boldToken = '@@DOCUMENT_BOLD_MARKER@@';

				return value
					.split('**')
					.join(boldToken)
					.split('*')
					.join('')
					.split(boldToken)
					.join('**');
			}

			return value;
		},

		containsSameInlineMarker(value, marker) {
			if (marker === '**') {
				return value.includes('**');
			}

			if (marker === '*') {
				const withoutBold = value.split('**').join('');

				return withoutBold.includes('*');
			}

			return false;
		},

		findContainingInlineFormatSection(line, startCh, endCh, marker) {
			let searchIndex = 0;

			while (searchIndex < line.length) {
				const open = line.indexOf(marker, searchIndex);

				if (open === -1) {
					return null;
				}

				if (marker === '*' && line.substring(open, open + 2) === '**') {
					searchIndex = open + 2;

					continue;
				}

				const contentStart = open + marker.length;
				const close = line.indexOf(marker, contentStart);

				if (close === -1) {
					return null;
				}

				const contentEnd = close;
				const sectionEnd = close + marker.length;

				if (startCh >= contentStart && endCh <= contentEnd) {
					return {
						sectionStart: open,
						sectionEnd,

						contentStart,
						contentEnd,
					};
				}

				searchIndex = sectionEnd;
			}

			return null;
		},

		wrapInlineFormatPart(value, marker) {
			if (!value || !value.trim()) {
				return value;
			}

			const { leadingWhitespace, trailingWhitespace, content } = this.splitOuterWhitespace(value);

			return leadingWhitespace + marker + content + marker + trailingWhitespace;
		},

		getStarRun(text, position) {
			if (position < 0 || position >= text.length || text[position] !== '*') {
				return null;
			}

			let start = position;
			let end = position + 1;

			while (start > 0 && text[start - 1] === '*') {
				start--;
			}

			while (end < text.length && text[end] === '*') {
				end++;
			}

			return {
				start,
				end,
				length: end - start,
			};
		},

		isInlineOpeningMarkerAt(text, position, marker) {
			const run = this.getStarRun(text, position);

			if (!run) {
				return false;
			}

			if (marker === '*') {
				if (run.length === 1) {
					return position === run.start;
				}

				if (run.length === 3) {
					return position === run.start;
				}

				return false;
			}

			if (marker === '**') {
				if (run.length === 2) {
					return position === run.start;
				}

				if (run.length === 3) {
					return position === run.start + 1;
				}
			}

			return false;
		},

		isInlineClosingMarkerBefore(text, position, marker) {
			if (position <= 0) {
				return false;
			}

			const run = this.getStarRun(text, position - 1);

			if (!run) {
				return false;
			}

			if (marker === '*') {
				if (run.length === 1) {
					return position === run.end;
				}

				if (run.length === 3) {
					return position === run.end;
				}

				return false;
			}

			if (marker === '**') {
				if (run.length === 2) {
					return position === run.end;
				}

				if (run.length === 3) {
					return position === run.start + 2;
				}
			}

			return false;
		},

		toggleList(type) {
			if (!this.editor) {
				return;
			}

			const codeMirror = this.editor.codemirror;
			const { startLine, endLine } = this.getSelectedLineRange(codeMirror);
			const allSelectedLinesAreSameType = this.areSelectedLinesListType(codeMirror, startLine, endLine, type);

			if (allSelectedLinesAreSameType) {
				this.removeListFormattingFromSelection(codeMirror, startLine, endLine, type);
			} else {
				this.addSelectionToList(codeMirror, startLine, endLine, type);
			}

			codeMirror.focus();
		},

		getListInfo(line) {
			const ordered = line.match(/^(\s*)(\d+)\.(?:\s+(.*))?$/);

			if (ordered) {
				return {
					type: 'ordered',
					indent: ordered[1],
					number: Number(ordered[2]),
					content: ordered[3] || '',
				};
			}

			const unordered = line.match(/^(\s*)[-+*](?:\s+(.*))?$/);

			if (unordered) {
				return {
					type: 'unordered',
					indent: unordered[1],
					content: unordered[2] || '',
				};
			}

			return null;
		},

		getListPrefixLength(line) {
			const ordered = line.match(/^(\s*\d+\.(?:\s+|$))/);

			if (ordered) {
				return ordered[1].length;
			}

			const unordered = line.match(/^(\s*[-+*](?:\s+|$))/);

			if (unordered) {
				return unordered[1].length;
			}

			return 0;
		},

		formatListLine(content, type, index, indent = '') {
			if (type === 'ordered') {
				return `${indent}${index + 1}. ` + content;
			}

			return `${indent}- ` + content;
		},

		getListBlock(codeMirror, lineNumber, type) {
			let start = lineNumber;
			let end = lineNumber;

			while (start > 0) {
				const info = this.getListInfo(codeMirror.getLine(start - 1));

				if (!info || info.type !== type) {
					break;
				}

				start--;
			}

			while (end < codeMirror.lineCount() - 1) {
				const info = this.getListInfo(codeMirror.getLine(end + 1));

				if (!info || info.type !== type) {
					break;
				}

				end++;
			}

			return { start, end };
		},

		getSelectedLineRange(codeMirror) {
			const from = codeMirror.getCursor('from');
			const to = codeMirror.getCursor('to');
			const startLine = from.line;
			const endLine = to.ch === 0 && to.line > from.line ? to.line - 1 : to.line;

			return {
				startLine,
				endLine,
			};
		},

		areSelectedLinesListType(codeMirror, startLine, endLine, type) {
			let hasContent = false;

			for (let line = startLine; line <= endLine; line++) {
				const text = codeMirror.getLine(line);

				if (!text.trim()) {
					continue;
				}

				hasContent = true;
				const info = this.getListInfo(text);

				if (!info || info.type !== type) {
					return false;
				}
			}

			return hasContent;
		},

		removeListFormattingFromSelection(codeMirror, startLine, endLine, type) {
			const ranges = [];

			let rangeStart = null;

			for (let line = startLine; line <= endLine; line++) {
				const text = codeMirror.getLine(line);
				const info = this.getListInfo(text);
				const isMatchingListLine = info && info.type === type;

				if (isMatchingListLine) {
					if (rangeStart === null) {
						rangeStart = line;
					}

					continue;
				}

				if (rangeStart !== null) {
					ranges.push({
						start: rangeStart,
						end: line - 1,
					});

					rangeStart = null;
				}
			}

			if (rangeStart !== null) {
				ranges.push({
					start: rangeStart,
					end: endLine,
				});
			}

			for (let index = ranges.length - 1; index >= 0; index--) {
				const range = ranges[index];

				this.removeSelectionFromList(codeMirror, range.start, range.end, type);
			}
		},

		renumberOrderedList(codeMirror, lineNumber) {
			const info = this.getListInfo(codeMirror.getLine(lineNumber));

			if (!info || info.type !== 'ordered') {
				return;
			}

			const block = this.getListBlock(codeMirror, lineNumber, 'ordered');

			for (let line = block.start; line <= block.end; line++) {
				const text = codeMirror.getLine(line);
				const current = this.getListInfo(text);

				if (!current) {
					continue;
				}

				const expected = this.formatListLine(current.content, 'ordered', line - block.start, current.indent);

				if (expected === text) {
					continue;
				}

				codeMirror.replaceRange(
					expected,
					{
						line,
						ch: 0,
					},
					{
						line,
						ch: text.length,
					},
					'+document-list-normalize',
				);
			}
		},

		normalizeOrderedLists(codeMirror) {
			let line = 0;

			while (line < codeMirror.lineCount()) {
				const info = this.getListInfo(codeMirror.getLine(line));

				if (!info || info.type !== 'ordered') {
					line++;
					continue;
				}

				const block = this.getListBlock(codeMirror, line, 'ordered');

				this.renumberOrderedList(codeMirror, block.start);

				line = block.end + 1;
			}
		},

		exitList(codeMirror, lineNumber, type) {
			const line = codeMirror.getLine(lineNumber);

			codeMirror.replaceRange(
				'\n',
				{
					line: lineNumber,
					ch: 0,
				},
				{
					line: lineNumber,
					ch: line.length,
				},
				'+document-list-exit',
			);

			codeMirror.setCursor({
				line: lineNumber + 1,
				ch: 0,
			});

			if (type === 'ordered') {
				this.normalizeOrderedLists(codeMirror);
			}
		},

		addSelectionToList(codeMirror, startLine, endLine, type) {
			let replaceStart = startLine;
			let replaceEnd = endLine;
			const beforeContents = [];
			const afterContents = [];

			if (startLine > 0) {
				const previousLine = startLine - 1;

				const previousInfo = this.getListInfo(codeMirror.getLine(previousLine));

				if (previousInfo && previousInfo.type === type) {
					const previousBlock = this.getListBlock(codeMirror, previousLine, type);

					replaceStart = previousBlock.start;

					for (let line = previousBlock.start; line < startLine; line++) {
						const info = this.getListInfo(codeMirror.getLine(line));

						if (info && info.type === type) {
							beforeContents.push(info.content);
						}
					}
				}
			}

			if (endLine < codeMirror.lineCount() - 1) {
				const nextLine = endLine + 1;
				const nextInfo = this.getListInfo(codeMirror.getLine(nextLine));

				if (nextInfo && nextInfo.type === type) {
					const nextBlock = this.getListBlock(codeMirror, nextLine, type);
					replaceEnd = nextBlock.end;

					for (let line = endLine + 1; line <= nextBlock.end; line++) {
						const info = this.getListInfo(codeMirror.getLine(line));

						if (info && info.type === type) {
							afterContents.push(info.content);
						}
					}
				}
			}

			const blocks = [];
			let currentBlock = [];

			const pushBlock = () => {
				if (!currentBlock.length) {
					return;
				}

				blocks.push(currentBlock);

				currentBlock = [];
			};

			if (beforeContents.length) {
				currentBlock.push(...beforeContents);
			}

			for (let line = startLine; line <= endLine; line++) {
				const text = codeMirror.getLine(line);

				if (!text.trim()) {
					pushBlock();

					continue;
				}

				const existing = this.getListInfo(text);
				currentBlock.push(existing ? existing.content : text.trim());
			}

			if (afterContents.length) {
				currentBlock.push(...afterContents);
			}

			pushBlock();

			if (!blocks.length) {
				return;
			}

			const resultBlocks = blocks.map(block => {
				return block.map((content, index) => this.formatListLine(content, type, index));
			});

			const result = resultBlocks
				.map(block => {
					return block.join('\n');
				})
				.join('\n\n');

			codeMirror.replaceRange(
				result,
				{
					line: replaceStart,
					ch: 0,
				},
				{
					line: replaceEnd,
					ch: codeMirror.getLine(replaceEnd).length,
				},
				'+document-list',
			);

			const resultLines = result.split('\n');
			const finalLine = replaceStart + resultLines.length - 1;

			codeMirror.setSelection(
				{
					line: replaceStart,
					ch: 0,
				},
				{
					line: finalLine,
					ch: resultLines[resultLines.length - 1].length,
				},
			);
		},

		removeSelectionFromList(codeMirror, startLine, endLine, type) {
			const block = this.getListBlock(codeMirror, startLine, type);

			const before = [];
			const plain = [];
			const after = [];

			for (let line = block.start; line <= block.end; line++) {
				const info = this.getListInfo(codeMirror.getLine(line));

				if (!info) {
					continue;
				}

				if (line < startLine) {
					before.push(info);
				} else if (line <= endLine) {
					plain.push(info.content);
				} else {
					after.push(info);
				}
			}

			const result = [];

			if (before.length) {
				result.push(...before.map((item, index) => this.formatListLine(item.content, type, index, item.indent)));
			}

			if (before.length && plain.length) {
				result.push('');
			}

			result.push(...plain);

			if (plain.length && after.length) {
				result.push('');
			}

			if (after.length) {
				result.push(...after.map((item, index) => this.formatListLine(item.content, type, index, item.indent)));
			}

			codeMirror.replaceRange(
				result.join('\n'),
				{
					line: block.start,
					ch: 0,
				},
				{
					line: block.end,
					ch: codeMirror.getLine(block.end).length,
				},
				'+document-list',
			);

			const plainStart = block.start + before.length + (before.length ? 1 : 0);
			const plainEnd = plainStart + plain.length - 1;

			if (plain.length) {
				codeMirror.setSelection(
					{
						line: plainStart,
						ch: 0,
					},
					{
						line: plainEnd,
						ch: result[plainEnd - block.start].length,
					},
				);
			}

			return {
				plainStart,
				plainEnd,
			};
		},

		normalizeOrderedListAroundLine(codeMirror, lineNumber) {
			const candidates = [lineNumber, lineNumber - 1, lineNumber + 1];

			for (const candidate of candidates) {
				if (candidate < 0 || candidate >= codeMirror.lineCount()) {
					continue;
				}

				const info = this.getListInfo(codeMirror.getLine(candidate));

				if (!info || info.type !== 'ordered') {
					continue;
				}

				this.renumberOrderedList(codeMirror, candidate);

				return;
			}
		},

		expandSelectionToDocumentElements(codeMirror, from, to) {
			const documentText = codeMirror.getValue();
			let start = codeMirror.indexFromPos(from);
			let end = codeMirror.indexFromPos(to);
			const placeholderPattern = /\{\{\s*[^{}]+?\s*\}\}/g;

			let match;

			while ((match = placeholderPattern.exec(documentText)) !== null) {
				const tokenStart = match.index;
				const tokenEnd = match.index + match[0].length;
				const overlaps = start < tokenEnd && end > tokenStart;

				if (!overlaps) {
					continue;
				}

				start = Math.min(start, tokenStart);
				end = Math.max(end, tokenEnd);
			}

			return {
				from: codeMirror.posFromIndex(start),
				to: codeMirror.posFromIndex(end),
			};
		},

		normalizeInlineFormatSelection(codeMirror, from, to, marker) {
			const text = codeMirror.getValue();
			let start = codeMirror.indexFromPos(from);
			let end = codeMirror.indexFromPos(to);

			while (start < end && /\s/.test(text[start])) {
				start++;
			}

			while (end > start && /\s/.test(text[end - 1])) {
				end--;
			}

			if (start >= end) {
				return {
					from: codeMirror.posFromIndex(start),
					to: codeMirror.posFromIndex(end),
				};
			}

			if (this.isInlineOpeningMarkerAt(text, start, marker)) {
				start += marker.length;

				while (start < end && /\s/.test(text[start])) {
					start++;
				}
			}

			if (this.isInlineClosingMarkerBefore(text, end, marker)) {
				end -= marker.length;

				while (end > start && /\s/.test(text[end - 1])) {
					end--;
				}
			}

			start = this.expandInlineStartToWordBoundary(text, start, marker);
			end = this.expandInlineEndToWordBoundary(text, end, marker);

			while (start < end && /\s/.test(text[start])) {
				start++;
			}

			while (end > start && /\s/.test(text[end - 1])) {
				end--;
			}

			return {
				from: codeMirror.posFromIndex(start),
				to: codeMirror.posFromIndex(end),
			};
		},

		expandSelectionToInlineFormat(codeMirror, from, to, marker) {
			const documentText = codeMirror.getValue();

			let start = codeMirror.indexFromPos(from);

			let end = codeMirror.indexFromPos(to);

			let changed = true;

			while (changed) {
				changed = false;

				const sections = this.findInlineFormatSections(documentText, marker);

				for (const section of sections) {
					const completelyInside = start >= section.contentStart && end <= section.contentEnd;

					if (completelyInside) {
						continue;
					}

					const entersFromLeft = start < section.start && end > section.start && end < section.end;
					const exitsToRight = start > section.start && start < section.end && end > section.end;
					const cutsOpeningMarker = start >= section.start && start < section.contentStart;
					const cutsClosingMarker = end > section.contentEnd && end <= section.end;

					let newStart = start;
					let newEnd = end;

					if (exitsToRight || cutsOpeningMarker) {
						newStart = Math.min(newStart, section.start);
					}

					if (entersFromLeft || cutsClosingMarker) {
						newEnd = Math.max(newEnd, section.end);
					}

					if (newStart !== start || newEnd !== end) {
						start = newStart;
						end = newEnd;
						changed = true;
					}
				}
			}

			while (start < end && /\s/.test(documentText[start])) {
				start++;
			}

			while (end > start && /\s/.test(documentText[end - 1])) {
				end--;
			}

			return {
				from: codeMirror.posFromIndex(start),

				to: codeMirror.posFromIndex(end),
			};
		},

		findInlineFormatSections(text, marker) {
			const sections = [];
			let openMarker = null;
			let position = 0;

			while (position < text.length) {
				if (text[position] !== '*') {
					position++;
					continue;
				}

				const run = this.getStarRun(text, position);

				if (!run) {
					position++;
					continue;
				}

				let markerPosition = null;

				if (marker === '*') {
					if (run.length === 1) {
						markerPosition = run.start;
					}

					if (run.length === 3) {
						markerPosition = openMarker === null ? run.start : run.end - 1;
					}
				}

				if (marker === '**') {
					if (run.length === 2) {
						markerPosition = run.start;
					}

					if (run.length === 3) {
						markerPosition = openMarker === null ? run.start + 1 : run.start;
					}
				}

				if (markerPosition === null) {
					position = run.end;
					continue;
				}

				if (openMarker === null) {
					openMarker = markerPosition;

					position = run.end;
					continue;
				}

				const contentStart = openMarker + marker.length;
				const contentEnd = markerPosition;

				if (contentEnd > contentStart) {
					sections.push({
						start: openMarker,
						end: markerPosition + marker.length,
						contentStart,
						contentEnd,
					});
				}

				openMarker = null;
				position = run.end;
			}

			return sections;
		},

		expandInlineStartToWordBoundary(text, position, marker) {
			let start = position;

			while (start > 0 && !/\s/.test(text[start - 1]) && text[start - 1] !== '\n') {
				start--;
			}

			if (this.isInlineOpeningMarkerAt(text, start, marker)) {
				start += marker.length;
			}

			return start;
		},

		expandInlineEndToWordBoundary(text, position, marker) {
			let end = position;

			while (end < text.length && !/\s/.test(text[end]) && text[end] !== '\n') {
				end++;
			}

			if (this.isInlineClosingMarkerBefore(text, end, marker)) {
				end -= marker.length;
			}

			return end;
		},

		insertText(value) {
			if (!this.editor || this.readonly) {
				return;
			}

			const codeMirror = this.editor.codemirror;
			const from = codeMirror.getCursor('from');
			const to = codeMirror.getCursor('to');
			codeMirror.replaceRange(value, from, to, '+document-element');

			const cursorIndex = codeMirror.indexFromPos(from) + value.length;
			codeMirror.setCursor(codeMirror.posFromIndex(cursorIndex));

			codeMirror.focus();
		},
	},
	watch: {
		readonly(value) {
			if (this.editor?.codemirror) {
				this.editor.codemirror.setOption('readOnly', value);
			}
		},
		elements: {
			handler() {
				if (!this.editor || !this.documentElementOverlay) {
					return;
				}

				const codeMirror = this.editor.codemirror;

				codeMirror.removeOverlay(this.documentElementOverlay);
				codeMirror.addOverlay(this.documentElementOverlay);

				codeMirror.refresh();
			},

			deep: true,
		},
	},
};
</script>
<style>
.document-markdown-editor {
	--document-element-highlight-background: #dbeafe;
	--document-element-highlight-color: #1e40af;
}

.document-markdown-editor .EasyMDEContainer {
	text-align: left;
}

.document-markdown-editor .editor-toolbar {
	border-color: #d1d5db;
	border-radius: 0.25rem 0.25rem 0 0;
	background: white;
	text-align: left;
}

.document-markdown-editor .CodeMirror {
	border-color: #d1d5db;
	border-radius: 0 0 0.25rem 0.25rem;
	height: 700px;
	min-height: 0;
	font-family: inherit;
	font-size: 14px;
	line-height: 1.5;
}

.document-markdown-editor .CodeMirror-scroll {
	height: 100%;
	min-height: 0;
	overflow-y: auto !important;
}

.document-markdown-editor .editor-toolbar button {
	min-width: 32px;
	font-size: 16px;
}

.document-markdown-editor .document-editor-bold {
	font-weight: bold;
}

.document-markdown-editor .document-editor-italic {
	font-style: italic;
}

.document-markdown-editor .document-editor-heading {
	font-weight: bold;
}

.document-markdown-editor .cm-document-element {
	background-color: var(--document-element-highlight-background);
	color: var(--document-element-highlight-color);
	border-radius: 3px;
}
</style>
